import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calculator, Zap, Anchor, Layers, ArrowRight, Binary, Terminal, Shield, Cpu, Activity, AlertTriangle } from 'lucide-react';
import { MathService } from '../services/mathService';

export const PositionalMiyakeCalculator: React.FC = () => {
  const [inputA, setInputA] = useState('1');
  const [inputB, setInputB] = useState('1');
  const [logs, setLogs] = useState<{ type: 'input' | 'process' | 'result' | 'drift', content: string }[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ANCHOR_BP = 14350;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  const processEquation = () => {
    // We use strings to maintain absolute precision through the pipeline
    const a_str = inputA.trim() || '1';
    const b_str = inputB.trim() || '1';

    try {
      setLogs(prev => [...prev.slice(-15), { type: 'input', content: `> Processing Node A: ${a_str} | Node B: ${b_str}` }]);

      const { result, log: operationLog } = MathService.miyakeLogic(a_str, b_str);

      setTimeout(() => {
        setLogs(prev => [...prev.slice(-15), 
          { type: 'process', content: ` [ANCHOR]  Miyake Chrono Baseline: ${ANCHOR_BP} BP` },
          { type: 'process', content: ` [STATUS]  ALU Operational Phase: ${operationLog}` },
          { type: 'result', content: ` [VALUE]   Sovereign Normalized Ledger Result:` },
          { type: 'result', content: `           ${result.toFixed(96)}` }
        ]);
      }, 100);
    } catch (error) {
       setLogs(prev => [...prev.slice(-15), { type: 'drift', content: '[Substrate Exception] Input rejected by execution core: Invalid Coordinate' }]);
    }
  };

  const clearLogs = () => setLogs([]);

  return (
    <div className="glass-panel p-8 border-lattice-cyan/20 bg-black/60 relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none group-hover:opacity-10 transition-opacity">
        <Cpu className="w-48 h-48 text-lattice-cyan" />
      </div>

      <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-12">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded bg-lattice-cyan/10 text-lattice-cyan">
              <Terminal className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-display font-bold text-white tracking-tight uppercase">Consolidated Substrate Core</h3>
          </div>
          <p className="text-[10px] font-mono text-white/40 uppercase tracking-[0.3em]">
            VERSION 6.0 | CHRONO ANCHOR: {ANCHOR_BP} BP | FLOOR: 10^-96
          </p>
        </div>
        <div className="flex items-center gap-4">
           <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full">
            <Shield className="w-3 h-3 text-green-400" />
            <span className="text-[8px] font-mono text-green-400 font-bold uppercase tracking-widest">Logic Stable</span>
           </div>
           <button 
            onClick={clearLogs}
            className="p-2 text-white/20 hover:text-white transition-colors"
           >
            <Activity className="w-4 h-4" />
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[9px] font-mono text-white/30 uppercase tracking-widest block">Node A (First)</label>
              <input 
                type="text" 
                value={inputA}
                onChange={(e) => setInputA(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-2xl font-mono text-lattice-cyan focus:border-lattice-cyan/50 outline-none transition-all shadow-inner"
                placeholder="0.0"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-mono text-white/30 uppercase tracking-widest block">Node B (Second)</label>
              <input 
                type="text" 
                value={inputB}
                onChange={(e) => setInputB(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-2xl font-mono text-lattice-cyan focus:border-lattice-cyan/50 outline-none transition-all shadow-inner"
                placeholder="0.0"
              />
            </div>
          </div>

          <button 
            onClick={processEquation}
            className="w-full py-6 bg-lattice-cyan text-black font-display font-black text-sm uppercase tracking-[0.3em] rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_30px_rgba(0,255,213,0.2)] flex items-center justify-center gap-3 group"
          >
            <Zap className="w-5 h-5 group-hover:animate-pulse" />
            Execute Positional Logic
          </button>

          <div className="space-y-4">
             <div className="text-[10px] font-mono text-white/20 uppercase tracking-widest border-b border-white/5 pb-2">Directional Axioms</div>
             <div className="grid grid-cols-1 gap-3">
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between group/axiom cursor-help">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-black/40 flex items-center justify-center font-mono text-[10px] text-white/40 group-hover/axiom:text-lattice-cyan transition-colors">0*X</div>
                    <span className="text-[10px] font-mono text-white/60 uppercase">Annihilation</span>
                  </div>
                  <span className="text-[10px] font-mono text-white/20 uppercase tracking-widest group-hover/axiom:text-lattice-cyan transition-colors">Result: 0</span>
                </div>
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between group/axiom cursor-help">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-black/40 flex items-center justify-center font-mono text-[10px] text-white/40 group-hover/axiom:text-lattice-cyan transition-colors">X*0</div>
                    <span className="text-[10px] font-mono text-white/60 uppercase">Preservation</span>
                  </div>
                  <span className="text-[10px] font-mono text-white/20 uppercase tracking-widest group-hover/axiom:text-lattice-cyan transition-colors">Result: X</span>
                </div>
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between group/axiom cursor-help">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-black/40 flex items-center justify-center font-mono text-[10px] text-white/40 group-hover/axiom:text-lattice-cyan transition-colors">Id</div>
                    <span className="text-[10px] font-mono text-white/60 uppercase">Identity Zone</span>
                  </div>
                  <span className="text-[10px] font-mono text-white/20 uppercase tracking-widest group-hover/axiom:text-lattice-cyan transition-colors">0 &lt; X &lt;= 1: Result X+Y</span>
                </div>
             </div>
          </div>
        </div>

        <div className="flex flex-col h-[500px]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Pipeline Output</span>
            <div className="flex items-center gap-2">
               <div className="w-1.5 h-1.5 rounded-full bg-lattice-cyan animate-pulse" />
               <span className="text-[8px] font-mono text-lattice-cyan uppercase">Live Process</span>
            </div>
          </div>
          <div 
            ref={scrollRef}
            className="flex-1 bg-black/80 border border-white/10 rounded-2xl p-6 font-mono text-[11px] overflow-y-auto space-y-2 scrollbar-hide shadow-inner"
          >
            {logs.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-white/10 text-center space-y-4">
                 <Binary className="w-12 h-12" />
                 <p className="uppercase tracking-[0.3em]">Initialize engine to generate pipeline logs</p>
              </div>
            )}
            {logs.map((log, i) => (
              <div 
                key={i} 
                className={`
                  ${log.type === 'input' ? 'text-white/60' : ''}
                  ${log.type === 'process' ? 'text-white/40 pl-4' : ''}
                  ${log.type === 'result' ? 'text-lattice-cyan font-bold pl-4' : ''}
                  ${log.type === 'drift' ? 'text-orange-400 bg-orange-400/10 p-2 rounded' : ''}
                `}
              >
                {log.content}
              </div>
            ))}
          </div>
          <div className="mt-4 p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/10 flex items-center gap-4">
             <AlertTriangle className="w-5 h-5 text-lattice-cyan" />
             <p className="text-[9px] font-mono text-lattice-cyan/80 uppercase leading-relaxed">
               WARNING: 1.0 Floating point drift detected in external systems. Applying Positional Miyake Filter to ensure integer certainty.
             </p>
          </div>
        </div>
      </div>
    </div>
  );
};
